import { Avatar, AvatarGroup, Tooltip } from '@mui/material';
import type { UserRef } from '@/lib/types';

const initials = (user: UserRef) =>
  `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase();

interface UserAvatarProps {
  user: UserRef;
  size?: number;
  showTooltip?: boolean;
}

export function UserAvatar({ user, size = 28, showTooltip = true }: UserAvatarProps) {
  const avatar = (
    <Avatar
      src={user.avatarUrl}
      sx={{
        width: size,
        height: size,
        bgcolor: user.avatarColor,
        fontSize: size * 0.4,
        color: '#fff',
      }}
    >
      {initials(user)}
    </Avatar>
  );

  if (!showTooltip) return avatar;
  return (
    <Tooltip title={`${user.firstName} ${user.lastName}`}>
      <span style={{ display: 'inline-flex' }}>{avatar}</span>
    </Tooltip>
  );
}

interface UserAvatarStackProps {
  users: UserRef[];
  size?: number;
  max?: number;
}

export function UserAvatarStack({ users, size = 26, max = 4 }: UserAvatarStackProps) {
  if (!users?.length) return null;
  return (
    <AvatarGroup
      max={max}
      sx={{
        '& .MuiAvatar-root': {
          width: size,
          height: size,
          fontSize: size * 0.38,
          borderWidth: 2,
        },
      }}
    >
      {/*
        Falls back to the index: some endpoints return assignees as raw id
        strings rather than populated refs, and `user.id` is then undefined for
        every entry — which collapses to one duplicate key across the stack.
      */}
      {users.map((user, index) => (
        <UserAvatar key={user?.id ?? `avatar-${index}`} user={user} size={size} />
      ))}
    </AvatarGroup>
  );
}
